import { assign, setup } from "xstate";
import type { WebStudioCachedStateClassification } from "../api/bootConfig";

/**
 * Canonical Web Studio flow machine, authored with the repository's
 * setup()-only XState v5 convention.
 *
 * The machine sequences which chrome mode the shell presents; it never
 * decides authorization, compatibility, or mutation eligibility itself.
 * Every BOOT_RESULT carries the backend-owned cached-state classification
 * computed by the generated-client layer, and guards only read that
 * classification's mode. The auth-shaped `unauthenticated` state (which
 * renders the sign-in gate) is reachable only through the backend's typed
 * `web_studio.auth.*` classification — from boot results or from
 * operation-level session-loss diagnostics (AUTH_LOST) — never from
 * browser heuristics.
 */

export type SessionMode =
  | "booting"
  | "fresh"
  | "readOnly"
  | "unauthenticated"
  | "blocked"
  | "failed";

interface SessionContext {
  diagnosticCode: string | null;
  failureMessage: string | null;
}

type SessionEvent =
  | { type: "BOOT_RESULT"; classification: WebStudioCachedStateClassification }
  | { type: "BOOT_FAILED"; message: string }
  | { type: "AUTH_LOST"; diagnosticCode: string }
  | { type: "SCOPE_CHANGED" }
  | { type: "REFRESH_REQUESTED" };

export const webStudioSessionMachine = setup({
  types: {
    context: {} as SessionContext,
    events: {} as SessionEvent,
  },
  guards: {
    classifiedFresh: ({ event }) =>
      event.type === "BOOT_RESULT" && event.classification.mode === "fresh",
    classifiedReadOnly: ({ event }) =>
      event.type === "BOOT_RESULT" && event.classification.mode === "read_only_cache",
    classifiedUnauthenticated: ({ event }) =>
      event.type === "BOOT_RESULT" && event.classification.mode === "unauthenticated",
  },
  actions: {
    rememberClassification: assign(({ event }) =>
      event.type === "BOOT_RESULT"
        ? { diagnosticCode: event.classification.diagnosticCode ?? null, failureMessage: null }
        : {},
    ),
    rememberFailure: assign(({ event }) =>
      event.type === "BOOT_FAILED" ? { diagnosticCode: null, failureMessage: event.message } : {},
    ),
    rememberAuthLoss: assign(({ event }) =>
      event.type === "AUTH_LOST"
        ? { diagnosticCode: event.diagnosticCode, failureMessage: null }
        : {},
    ),
    clearContext: assign({ diagnosticCode: null, failureMessage: null }),
  },
}).createMachine({
  id: "webStudioSession",
  initial: "booting",
  context: { diagnosticCode: null, failureMessage: null },
  // Every state re-classifies on a fresh boot result so focus-driven and
  // cross-tab boot refreshes reconcile without an intermediate event, and
  // operation-level typed session loss routes to the gate from anywhere.
  on: {
    BOOT_RESULT: [
      { guard: "classifiedFresh", target: ".fresh", actions: "rememberClassification" },
      { guard: "classifiedReadOnly", target: ".readOnly", actions: "rememberClassification" },
      {
        guard: "classifiedUnauthenticated",
        target: ".unauthenticated",
        actions: "rememberClassification",
      },
      { target: ".blocked", actions: "rememberClassification" },
    ],
    BOOT_FAILED: { target: ".failed", actions: "rememberFailure" },
    AUTH_LOST: { target: ".unauthenticated", actions: "rememberAuthLoss" },
    SCOPE_CHANGED: { target: ".booting" },
    REFRESH_REQUESTED: { target: ".booting" },
  },
  states: {
    booting: {
      entry: "clearContext",
    },
    fresh: {},
    readOnly: {},
    unauthenticated: {
      on: {
        // A refresh while gated (focus/cross-tab reconciliation, retry) must
        // not bounce through `booting`: leaving the state unmounts the gate
        // and destroys the in-progress email-code entry. The boot query still
        // refetches; the next BOOT_RESULT re-classifies from here.
        REFRESH_REQUESTED: {},
      },
    },
    blocked: {},
    failed: {},
  },
});
